import { z } from 'zod';
import { OrganizationUserRoleSchema } from '../types/organizationUserRole';
import { TeamMemberRole, TeamMemberRoleSchema } from '../types/teamMemberRole';

export enum OrganizationAccessScope {
  SELF = 'SELF',
  ANY_USER = 'ANY_USER',
  UNOWNED = 'UNOWNED',
}

export const OrganizationAccessScopeSchema = z.nativeEnum(
  OrganizationAccessScope
);

export enum TeamAccessScope {
  SELF = 'SELF',
  TEAM = 'TEAM',
}

export const TeamAccessScopeSchema = z.nativeEnum(TeamAccessScope);

export enum Access {
  READ = 'READ',
  WRITE = 'WRITE',
  ALL = 'ALL',
}

export const AccessSchema = z.nativeEnum(Access);

export enum Resource {
  ALL = 'ALL',
  ACTION_DEFINITIONS = 'ACTION_DEFINITIONS',
  ACTION_EXECUTIONS = 'ACTION_EXECUTIONS',
  ACTION_TRIGGERS = 'ACTION_TRIGGERS',
  AI_PHONE_NUMBERS = 'AI_PHONE_NUMBERS',
  CARRIERS = 'CARRIERS',
  CONTACTS = 'CONTACTS',
  JOBS = 'JOBS',
  DOCUMENTS = 'DOCUMENTS',
  ORDERS = 'ORDERS',
  ORIGINATORS = 'ORIGINATORS',
  PLACES = 'PLACES',
  PRICING_STRATEGIES = 'PRICING_STRATEGIES',
  QUOTES = 'QUOTES',
  USERS = 'USERS',
  EVENTS = 'EVENTS',
  INTEGRATIONS = 'INTEGRATIONS',
  MESSAGES = 'MESSAGES',
  EMAILS = 'EMAILS',
  EXTERNAL_MESSAGES = 'EXTERNAL_MESSAGES',
  CONVERSATIONS = 'CONVERSATIONS',
  SHIPMENTS = 'SHIPMENTS',
  MOVEMENTS = 'MOVEMENTS',
  TEAMS = 'TEAMS',
  TEAM_MEMBERS = 'TEAM_MEMBERS',
  KNOWLEDGE_BASES = 'KNOWLEDGE_BASES',
  SHARED_FILES = 'SHARED_FILES',
  NEGOTIATION_STRATEGIES = 'NEGOTIATION_STRATEGIES',
  WORKERS = 'WORKERS',
  KNOWLEDGE_BASE_DOCUMENT_UPLOADS = 'KNOWLEDGE_BASE_DOCUMENT_UPLOADS',
  EXTERNAL_CHANNELS = 'EXTERNAL_CHANNELS',
  EXTERNAL_ID_MAPPINGS = 'EXTERNAL_ID_MAPPINGS',
  FREIGHT_DATA_MAPPINGS = 'FREIGHT_DATA_MAPPINGS',
  SAVED_VIEW = 'SAVED_VIEW',
  WEBHOOK_SUBSCRIPTIONS = 'WEBHOOK_SUBSCRIPTIONS',
  API_CLIENT = 'API_CLIENT',
  EXCEPTIONS = 'EXCEPTIONS',
  GENERIC_RULES = 'GENERIC_RULES',
  AUTH_TOKEN_PREVIEWS = 'AUTH_TOKEN_PREVIEWS',
}

export const ResourceSchema = z.nativeEnum(Resource);

export type Permission = {
  access: Access;
  resource: Resource;
};

export class Permissions {
  static readonly all: Permission = {
    access: Access.ALL,
    resource: Resource.ALL,
  };

  static readonly readAll: Permission = {
    access: Access.READ,
    resource: Resource.ALL,
  };

  static readonly writeAll: Permission = {
    access: Access.WRITE,
    resource: Resource.ALL,
  };
}

export const ScopedPermissionSchema = z.discriminatedUnion('scope', [
  z.object({
    scope: z.literal('UNOWNED'),
    resource: ResourceSchema,
    access: AccessSchema,
    organizationId: z.string(),
  }),
  z.object({
    scope: z.literal('ANY_USER'),
    resource: ResourceSchema,
    access: AccessSchema,
    organizationId: z.string(),
  }),
  z.object({
    scope: z.literal('TEAM'),
    resource: ResourceSchema,
    access: AccessSchema,
    teamId: z.string(),
  }),
  z.object({
    scope: z.literal('SELF'),
    resource: ResourceSchema,
    access: AccessSchema,
    userId: z.string(),
  }),
]);

export type ScopedPermission = z.infer<typeof ScopedPermissionSchema>;

function encodeAccess(access: Access) {
  switch (access) {
    case Access.READ:
      return 1;
    case Access.WRITE:
      return 2;
    case Access.ALL:
      return 3;
    default:
      throw new Error(`Unknown access level: ${access}`);
  }
}

export const isSufficientAccess = (
  access: Access,
  requested: Access
): boolean => {
  return encodeAccess(access) >= encodeAccess(requested);
};

const TeamScopeMapSchema = z.record(TeamAccessScopeSchema, AccessSchema);
export const TeamRoleMapSchema = z.record(
  TeamMemberRoleSchema,
  TeamScopeMapSchema
);
const TeamPolicySchema = z.record(ResourceSchema, TeamRoleMapSchema);

export type TeamScopeMap = z.infer<typeof TeamScopeMapSchema>;
export type TeamRoleMap = z.infer<typeof TeamRoleMapSchema>;
export type TeamPolicy = z.infer<typeof TeamPolicySchema>;

const OrganizationScopeMapSchema = z.record(
  OrganizationAccessScopeSchema,
  AccessSchema
);
export const OrganizationRoleMapSchema = z.record(
  OrganizationUserRoleSchema,
  OrganizationScopeMapSchema
);
const OrganizationPolicySchema = z.record(
  ResourceSchema,
  OrganizationRoleMapSchema
);

export type OrganizationScopeMap = z.infer<typeof OrganizationScopeMapSchema>;
export type OrganizationRoleMap = z.infer<typeof OrganizationRoleMapSchema>;
export type OrganizationPolicy = z.infer<typeof OrganizationPolicySchema>;

const RoleHierarchySchema = z.record(
  OrganizationUserRoleSchema,
  z.array(OrganizationUserRoleSchema)
);

export type RoleHierarchy = z.infer<typeof RoleHierarchySchema>;

export const PolicySuiteSchema = z.object({
  organizationPolicy: OrganizationPolicySchema,
  defaultTeamPolicy: TeamPolicySchema,
  roleHierarchy: RoleHierarchySchema,
  fallbackOrganizationPolicy: OrganizationRoleMapSchema.optional(),
  fallbackTeamPolicy: TeamRoleMapSchema.optional(),
});

export type PolicySuite = z.infer<typeof PolicySuiteSchema>;

export type TeamMembership = {
  teamId: string;
  role: TeamMemberRole;
  policy?: TeamPolicy;
};
